import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Profile } from '@lr/shared';
import { apiFetch } from '../../lib/api';

/**
 * Quick credit-adjustment screen — for the same flow inline on a customer
 * detail page (with audit context), but useful as a standalone tool when an
 * admin already knows which customer they want to credit.
 */
export function AdminCredits() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [userId, setUserId] = useState('');
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState<'admin_adjustment' | 'fit_return_refund'>(
    'admin_adjustment'
  );
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isFitReturn = kind === 'fit_return_refund';

  useEffect(() => {
    apiFetch<Profile[]>('/api/admin/users').then(setUsers);
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMsg(null);
    setSaving(true);
    try {
      if (isFitReturn && amount <= 0) {
        throw new Error('Fit-return refunds must add credits (positive amount).');
      }
      await apiFetch('/api/admin/credits/adjust', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, amount, notes, kind })
      });
      setMsg(
        isFitReturn
          ? 'Fit-return refund saved. Customer notified and granted a delivery-fee exemption.'
          : 'Credit adjustment saved. Customer notified by email.'
      );
      setAmount(0);
      setNotes('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="admin-page-head">
        <div>
          <h1>Adjust credits</h1>
          <div className="sub">
            Add or remove credits from a customer&apos;s balance. They&apos;re emailed
            automatically.
          </div>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}
      {msg && <div className="a-alert a-alert-good">{msg}</div>}

      <div className="a-card" style={{ maxWidth: 560 }}>
        <div className="a-card-body">
          <form onSubmit={onSubmit} className="a-form">
            <div className="a-field">
              <label>Customer *</label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} required>
                <option value="">— Select a user —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} · {u.credit_balance}{' '}
                    {u.credit_balance === 1 ? 'credit' : 'credits'}
                    {u.role !== 'user' ? ` · ${u.role.replace('_', ' ')}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="a-field">
              <label>Reason type *</label>
              <select
                value={kind}
                onChange={(e) =>
                  setKind(
                    e.target.value as 'admin_adjustment' | 'fit_return_refund'
                  )
                }
              >
                <option value="admin_adjustment">
                  Goodwill / general adjustment
                </option>
                <option value="fit_return_refund">FIT RETURN refund</option>
              </select>
              <div className="help">
                {isFitReturn
                  ? 'For items returned because they didn’t fit. The customer gets a one-off £4.99 delivery exemption so a 1- or 2-credit basket can ship.'
                  : 'For goodwill credits, mistakes, promotions, etc.'}
              </div>
            </div>
            <div className="a-field">
              <label>Amount *</label>
              <input
                type="number"
                value={amount || ''}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                placeholder={isFitReturn ? 'e.g. 1' : 'e.g. 3 (add) or -1 (remove)'}
                min={isFitReturn ? 1 : undefined}
                required
              />
              <div className="help">
                {isFitReturn
                  ? 'Number of credits to refund — must be positive.'
                  : 'Positive number adds credits, negative removes them.'}
              </div>
            </div>
            <div className="a-field">
              <label>Note *</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  isFitReturn
                    ? 'e.g. Customer returned the navy blazer — wrong fit.'
                    : 'Goodwill gesture for delayed delivery'
                }
                rows={3}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Link to="/admin/customers" className="btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving || !userId || !amount || !notes.trim()}
              >
                {saving
                  ? 'Saving…'
                  : isFitReturn
                    ? 'Refund as fit return'
                    : 'Adjust credits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
